import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Eye, EyeSlash, LockFill, CheckCircleFill, ExclamationTriangleFill, ShieldLockFill } from 'react-bootstrap-icons';
import { studentAPI } from '../services/api';
import LockIcon from '../img/lock.gif'; 
import '../CSS/Students.css';

 /* ── Password field renderer ── */
const PasswordField = ({ name, label, show, onToggle, value, onChange, error }) => (
  <div className="stu-field-group">
    <div className="chp-input-wrapper">
      <LockFill className="chp-field-icon" />
      <input
        type={show ? 'text' : 'password'}
        name={name}
        value={value}
        onChange={onChange}
        className={`stu-form-input chp-padded-input${error ? ' stu-input-invalid' : ''}`}
        placeholder=" "
        autoComplete="new-password"
        data-has-value={value ? 'true' : 'false'}
      />
      <label className={`stu-field-label chp-field-label${value ? ' stu-field-label--float' : ''}`}>
        {label}
      </label>
      <button type="button" className="chp-eye-btn" onClick={onToggle} tabIndex={-1}>
        {show ? <EyeSlash /> : <Eye />}
      </button>
    </div>
    {error && <div className="stu-field-error">{error}</div>}
  </div>
);
const ChangePassword = () => {
  const { user } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showCurrent, setShowCurrent]     = useState(false);
  const [showNew, setShowNew]             = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [loading, setLoading]             = useState(false);
  const [toast, setToast]                 = useState({ show: false, message: '', variant: 'success' });

  /* ── Auto-dismiss toast ── */
  useEffect(() => {
    if (toast.show) {
      const id = setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
      return () => clearTimeout(id);
    }
  }, [toast.show]);

  const showNotification = (message, variant) => setToast({ show: true, message, variant });

  /* ── Validation ── */
  const validate = () => {
    const errors = {};
    if (!formData.currentPassword.trim()) errors.currentPassword = 'Current password is required.';
    if (!formData.newPassword.trim()) {
      errors.newPassword = 'New password is required.';
    } else if (formData.newPassword.length < 8) {
  errors.newPassword = 'Password must be at least 8 characters.';
}
    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password.';
    } else if (formData.newPassword !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }
    return errors;
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) { setValidationErrors(errors); return; }
    setValidationErrors({});
    setLoading(true);
    try {
      await studentAPI.changePassword({
        id: user.id,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      showNotification('Password changed successfully.', 'success');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      showNotification(error.response?.data?.message || 'Failed to change password.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setValidationErrors(prev => ({ ...prev, [name]: undefined }));
  };

 

  /* ═══════════════════════════════════════
     RENDER
  ═══════════════════════════════════════ */
  return (
    <>
      <div className="stu-page">

        {/* ── Header ── */}
        <div className="row my-4">
          <div className="col-12">
            <h3>
              Change Password
              <img className="animated-icons" src={LockIcon} alt="Change Password icon" />
            </h3>
          </div>
        </div>

        {/* ── Main content ── */}
        <div className="stu-main-container">
          <div className="stu-main-content">

            <h4 className="stu-section-title">Update Your Password</h4>

            <div className="chp-card">

              {/* Shield icon banner */}
              <div className="chp-shield-banner">
                <ShieldLockFill className="chp-shield-icon" />
                <div>
                  <p className="chp-banner-title">Secure Password Update</p>
                  <p className="chp-banner-sub">
                    Enter your current password to verify your identity, then choose a new password.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} noValidate>
                <div className="chp-form-grid">

                  <PasswordField
                  name="currentPassword"
                  label="Current Password"
                  show={showCurrent}
                  onToggle={() => setShowCurrent(v => !v)}
                  value={formData.currentPassword}
                  onChange={handleChange}
                  error={validationErrors.currentPassword}
                />

                <PasswordField
                  name="newPassword"
                  label="New Password"
                  show={showNew}
                  onToggle={() => setShowNew(v => !v)}
                  value={formData.newPassword}
                  onChange={handleChange}
                  error={validationErrors.newPassword}
                />

                <PasswordField
                  name="confirmPassword"
                  label="Confirm New Password"
                  show={showConfirm}
                  onToggle={() => setShowConfirm(v => !v)}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={validationErrors.confirmPassword}
                />

                </div>

                <div className="stu-form-submit-row">
                  <button
                    type="submit"
                    className="stu-btn stu-btn-success stu-btn-submit"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>

      {/* ── Toast ── */}
      <div className={`stu-toast stu-toast-${toast.variant}${toast.show ? ' stu-toast-show' : ''}`}>
        <div className="stu-toast-header">
          <strong className="stu-toast-title">
            {toast.variant === 'success' ? 'Success' : 'Error'}
          </strong>
          <button className="stu-toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>×</button>
        </div>
        <div className="stu-toast-body">
          {toast.variant === 'success'
            ? <CheckCircleFill className="stu-toast-icon" />
            : <ExclamationTriangleFill className="stu-toast-icon" />}
          {toast.message}
        </div>
      </div>
    </>
  );
};

export default ChangePassword;